import { convertDateTime, errorMessage, formatDateTime } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useToasts } from "react-toast-notifications";
import transportLocalitiesService from "services/transportLocalitiesService";
import { formConvertErrorMessage } from "../../helpers/form_helper";

export const useListTransportLocalities = (payload, onSuccess, onError) => {
  const { params, queryKey, useToDisalbeCalendar } = payload;
  return useQuery(
    [queryKey || "transport-localities-list", params?.page],
    () => transportLocalitiesService.getTransportLocalities(payload),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: Object({ data: [] }),
      select: response => {
        const { data } = response;
        if (!data?.items) {
          return {
            items: [],
          };
        }
        if (useToDisalbeCalendar) {
          return {
            items:
              data?.items.map(i => {
                return convertDateTime(
                  i.from_date,
                  formatDateTime.YYYY_MM_DD,
                  "-"
                );
              }) || [],
          };
        } else {
          return {
            items: data?.items || [],
            params: params,
            totalCount: data._meta
              ? data._meta.totalCount
              : data?.items?.length,
          };
        }
      },
    }
  );
};

// Detail transportLocalities
export const useDetailTransportLocalities = (payload, onSuccess, onError) => {
  return useQuery(
    ["transportLocalities", "detail", payload],
    () => transportLocalitiesService.detailTransportLocalities(payload),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return data;
      },
    }
  );
};

// Create TransportLocalities
export const useCreateTransportLocalities = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["transportLocalities"]);
  const { addToast } = useToasts();

  return useMutation(transportLocalitiesService.createTransportLocalities, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.create", {
          target: t("transportLocalities"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.create", {
              target: t("transportLocalities"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Update TransportLocalities
export const useUpdateTransportLocalities = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["transportLocalities"]);
  const { addToast } = useToasts();

  return useMutation(transportLocalitiesService.updateTransportLocalities, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.update", {
          target: t("transportLocalities"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.update", {
              target: t("transportLocalities"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Delete TransportLocalities
export const useDeleteTransportLocalities = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["transportLocalities"]);
  const { addToast } = useToasts();

  return useMutation(transportLocalitiesService.deleteTransportLocalities, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.delete", {
          target: t("transportLocalities"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.delete", {
              target: t("transportLocalities"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};
